import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLoaderData } from 'react-router-dom';
import {
	CCard, CCardBody, CCardHeader, CCol, CRow, CButton,
	CFormLabel, CFormInput, CFormSelect, CBadge,
	CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
} from '@coreui/react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AxiosInstance from 'src/components/Axios';
import { CenterLoader } from 'src/components/myComponents/CenterLoader';
import { cilSearch, cilPencil } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import { STATUS_COLORS, STATUS_LABELS, SESSION_STATUS_OPTIONS, formatDateTime } from 'src/constants/supportLetters';
import {
	patchCachedSession, setCachedSession,
} from 'src/utils/supportRequestCache';
import { useSupportCategories } from 'src/hooks/useSupportCategories';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import { MediaViewerButton } from 'src/components/myComponents/MediaViewerModal';
import SupportLetterReplyModal from './SupportLetterReplyModal';
import SupportLettersModal from './SupportLettersModal';
import SupportChangeSessionModal from './SupportChangeSessionModal';
import SupportChangeParentModal from './SupportChangeParentModal';

const urlApiSessions = '/support-sessions';
const urlSupportLetters = '/support-letters';

export async function LoaderSupportLettersSession({ params }) {
	return AxiosInstance.get(`${urlApiSessions}/${params.id}`);
}


const SupportLettersSession = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const loaderData = useLoaderData();

	const allCategories = useSupportCategories();
	const catOptions = allCategories
		.filter(c => c.scopes?.some(s => s === 'conversation' || s === 'all'))
		.map(c => ({ value: c.id, label: c.name }));

	const [session, setSession] = useState(loaderData?.data ?? null);
	const [requests, setRequests] = useState(loaderData?.data?.requests ?? []);
	const [loading, setLoading] = useState(false);

	// Статус сесії
	const [sessionStatus, setSessionStatus] = useState(session?.status || 'open');
	const [savedSessionStatus, setSavedSessionStatus] = useState(session?.status || 'open');

	// Категорії сесії
	const [sessionCategories, setSessionCategories] = useState([]);
	const [initialCategories, setInitialCategories] = useState([]);

	const [saving, setSaving] = useState(false);

	const [sessionClientData] = useState({
		user_email: loaderData?.data?.user_email || '',
		user_name: loaderData?.data?.user_name || '',
		user_phone: loaderData?.data?.user_phone || '',
	});

	// Modal: відповісти / додати лист
	const [replyModalOpen, setReplyModalOpen] = useState(false);
	const [parentRequest, setParentRequest] = useState(null);

	// Modal: змінити сесію (для конкретного request)
	const [changeSessionRequest, setChangeSessionRequest] = useState(null);

	// Modal: перемістити лист в дереві (змінити батьківський лист)
	const [changeParentRequest, setChangeParentRequest] = useState(null);

	// Modal: вибір після переміщення — залишитися чи перейти
	const [sessionChoiceModal, setSessionChoiceModal] = useState(null);

	// Modal: швидкий перегляд листа
	const [modalOpen, setModalOpen] = useState(false);
	const [modalRequestId, setModalRequestId] = useState(null);

	// Синхронізація стейту при навігації між сесіями (той самий route, новий loaderData)
	useEffect(() => {
		setSession(loaderData?.data ?? null);
		setSessionChoiceModal(null);
	}, [loaderData]);

	useEffect(() => {
		if (session) {
			setSessionStatus(session.status || 'open');
			setSavedSessionStatus(session.status || 'open');
			setRequests(session.requests || []);
			const mapped = (session.categories || []).map(c => ({ value: c.id, label: c.name }));
			setSessionCategories(mapped);
			setInitialCategories(mapped);
		}
	}, [session]);

	// ── Computed ─────────────────────────────────────────────────────────────────

	const statusChanged = sessionStatus !== savedSessionStatus;
	const categoriesChanged = sessionCategories.map(c => c.value).sort().join(',')
		!== initialCategories.map(c => c.value).sort().join(',');
	const hasChanges = statusChanged || categoriesChanged;

	// ── Зберегти / Скасувати ─────────────────────────────────────────────────────

	const handleSave = async () => {
		const doStatus = statusChanged;
		const doCategories = categoriesChanged;
		const errors = [];
		setSaving(true);

		if (doStatus) {
			try {
				const res = await AxiosInstance.put(`${urlApiSessions}/${id}/status`, { status: sessionStatus });
				const newStatus = res.data?.status || sessionStatus;
				setSavedSessionStatus(newStatus);
				patchCachedSession(id, s => ({ ...s, status: newStatus }));
			} catch {
				errors.push('статус сесії');
			}
		}

		if (doCategories) {
			try {
				const added = sessionCategories.filter(n => !initialCategories.some(p => p.value === n.value));
				const removed = initialCategories.filter(p => !sessionCategories.some(n => n.value === p.value));
				for (const c of added)
					await AxiosInstance.post(`${urlApiSessions}/${id}/support-categories`, { category_id: c.value });
				for (const c of removed)
					await AxiosInstance.delete(`${urlApiSessions}/${id}/support-categories/${c.value}`);
				setInitialCategories(sessionCategories);
				patchCachedSession(id, s => ({
					...s,
					categories: sessionCategories.map(c => ({ id: c.value, name: c.label })),
				}));
			} catch {
				errors.push('категорії сесії');
			}
		}

		if (errors.length === 0) {
			toast.success('Збережено');
		} else {
			toast.error(`Помилка збереження: ${errors.join(', ')}. Решта змін збережено.`);
		}
		setSaving(false);
	};

	const handleCancel = () => {
		setSessionStatus(savedSessionStatus);
		setSessionCategories(initialCategories);
	};

	// ── Відповісти / Додати лист ────────────────────────────────────────────────

	const openReplyModal = (req) => {
		setParentRequest(req);
		setReplyModalOpen(true);
	};

	const openAddModal = () => {
		setParentRequest(null);
		setReplyModalOpen(true);
	};

	// ── Швидкий перегляд ────────────────────────────────────────────────────────

	const handleOpenModal = (reqId) => {
		setModalRequestId(reqId);
		setModalOpen(true);
	};


	// ── Після переміщення листа до іншої сесії ──────────────────────────────────

	const handleChangeSessionSuccess = async ({ newSessionId, newSessionSubject }) => {
		setLoading(true);
		try {
			const res = await AxiosInstance.get(`${urlApiSessions}/${id}`);
			setSession(res.data);
			setCachedSession(id, res.data);
			if (newSessionId && newSessionId !== parseInt(id)) {
				setSessionChoiceModal({ newSessionId, newSessionSubject });
			}
		} catch {
			if (newSessionId) {
				navigate(`${urlSupportLetters}/session/${newSessionId}`);
			} else {
				navigate(urlSupportLetters);
			}
		} finally {
			setLoading(false);
		}
	};

	// ── Після переміщення листа в дереві (зміна батьківського листа) ───────────────

	const handleChangeParentSuccess = async () => {
		setLoading(true);
		try {
			const res = await AxiosInstance.get(`${urlApiSessions}/${id}`);
			setSession(res.data);
			setCachedSession(id, res.data);
		} finally {
			setLoading(false);
		}
	};

	// ── Дерево листів ────────────────────────────────────────────────────────────

	const { childrenMap, roots } = useMemo(() => {
		const map = {};
		requests.forEach(r => {
			const key = r.parent_id ?? 'root';
			if (!map[key]) map[key] = [];
			map[key].push(r);
		});
		Object.values(map).forEach(arr =>
			arr.sort((a, b) => new Date(a.sent_at || a.created_at) - new Date(b.sent_at || b.created_at))
		);
		const requestIds = new Set(requests.map(r => r.id));
		const rootItems = requests.filter(r => !r.parent_id || !requestIds.has(r.parent_id));
		rootItems.sort((a, b) => new Date(a.sent_at || a.created_at) - new Date(b.sent_at || b.created_at));
		return { childrenMap: map, roots: rootItems };
	}, [requests]);

	const renderRequestNode = (req, depth = 0) => {
		const children = childrenMap[req.id] || [];
		return (
			<div key={req.id}>
				<div style={depth > 0 ? { marginLeft: depth * 24, paddingLeft: 12, borderLeft: '2px solid #dee2e6' } : {}}>
					<div
						className={`p-3 border rounded${depth > 0 ? ' mt-2' : ''}`}
						style={{ borderLeft: req.sender_type === 'admin' ? '4px solid #f9b115' : '4px solid #39f' }}
					>
						<div className="d-flex justify-content-between align-items-start mb-2">
							<div className="d-flex gap-2 align-items-center flex-wrap">
								<CBadge color={req.sender_type === 'admin' ? 'warning' : 'info'}>
									{req.sender_type === 'admin' ? 'Адмін' : 'Юзер'}
								</CBadge>
								<strong>{req.user_email}</strong>
								<span className="text-muted small">{formatDateTime(req.sent_at || req.created_at)}</span>
								<CBadge color={STATUS_COLORS[req.status] || 'secondary'}>
									{STATUS_LABELS[req.status] || req.status}
								</CBadge>
								{req.channel?.name && (
									<span className="text-muted small">Канал: {req.channel.name}</span>
								)}
							</div>
							<span className="text-muted small">#{req.id}</span>
						</div>
						{(req.responders || []).length > 0 && (
							<div className="mb-2">
								<span className="text-muted small me-1">Команди відповідальних:</span>
								{req.responders.map(r => (
									<CBadge key={r.team_id} color="info" className="me-1">
										{r.team_name} ({r.team_code})
									</CBadge>
								))}
							</div>
						)}
						<div
							className="mb-3"
							style={{ whiteSpace: 'pre-wrap', background: '#f8f9fa', padding: '8px', borderRadius: '4px', maxHeight: '120px', overflowY: 'auto' }}
						>
							{req.message || '—'}
						</div>
						<div className="d-flex gap-2 flex-wrap">
							{(req.attachments || []).length > 0 && (
								<MediaViewerButton
									attachments={req.attachments}
									className="media-viewer-button--bordered"
									title="Переглянути вкладення листа"
								/>
							)}
							<button
								className="custom-view-icon"
								title="Швидкий перегляд листа"
								onClick={() => handleOpenModal(req.id)}
								style={{ background: 'none', border: '1px solid #6c757d', borderRadius: '0.2rem', padding: '0.25rem 0.5rem', cursor: 'pointer', lineHeight: 1 }}
								onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#6c757d'; e.currentTarget.style.color = '#fff'; }}
								onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = ''; }}
							>
								👁
							</button>
							<Link to={`${urlSupportLetters}/view/${req.id}`} title="Переглянути повну сторінку листа">
								<CButton color="secondary" variant="outline" size="sm">
									<CIcon icon={cilSearch} height={14} className="me-1" />
									Переглянути
								</CButton>
							</Link>
							<Link to={`${urlSupportLetters}/update/${req.id}`} title="Редагувати лист">
								<CButton color="secondary" variant="outline" size="sm">
									<CIcon icon={cilPencil} height={14} className="me-1" />
									Редагувати
								</CButton>
							</Link>
							<CButton
								color="primary"
								variant="outline"
								size="sm"
								onClick={() => openReplyModal(req)}
								title="Додати відповідь на цей лист"
							>
								Відповісти
							</CButton>
							<CButton
								color="warning"
								variant="outline"
								size="sm"
								onClick={() => setChangeSessionRequest(req)}
								title="Перемістити лист до іншої сесії"
							>
								Змінити сесію
							</CButton>
							<CButton
								color="secondary"
								variant="outline"
								size="sm"
								onClick={() => setChangeParentRequest(req)}
								disabled={requests.length <= 1}
								title={requests.length <= 1
									? 'В сесії лише один лист — переміщувати нема куди'
									: 'Перемістити лист в іншу гілку дерева сесії (змінити батьківський лист)'}
							>
								Перемістити в дереві
							</CButton>
						</div>
					</div>
				</div>
				{children.map(child => renderRequestNode(child, depth + 1))}
			</div>
		);
	};

	// ── Render ──────────────────────────────────────────────────────────────────

	if (!session) {
		return (
			<CCard className="mb-4">
				<CCardBody>
					<p className="text-danger">Сесію не знайдено.</p>
					<CButton color="secondary" variant="outline" onClick={() => navigate(-1)} title="Повернутись">
						← Назад
					</CButton>
				</CCardBody>
			</CCard>
		);
	}

	return (
		<>
			<ToastContainer />

			{/* ── Заголовок сесії ── */}
			<CCard className="mb-4">
				<CCardHeader>
					<strong>Сесія #{session.id}</strong>
					{session.subject && <span className="ms-2 text-muted">— {session.subject}</span>}
				</CCardHeader>
				<CCardBody>
					<FormSubmitOverlay submitting={saving}>
						<CRow className="g-3">
							<CCol md={4}>
								<CFormLabel>Email клієнта</CFormLabel>
								<CFormInput value={session.user_email || '—'} disabled readOnly />
							</CCol>
							<CCol md={3}>
								<CFormLabel>Статус сесії</CFormLabel>
								<CFormSelect
									value={sessionStatus}
									onChange={e => setSessionStatus(e.target.value)}
									disabled={saving}
									title="Вибрати статус сесії"
								>
									{SESSION_STATUS_OPTIONS.map(o => (
										<option key={o.value} value={o.value}>{o.label}</option>
									))}
								</CFormSelect>
							</CCol>
							<CCol md={5}>
								<CFormLabel>Категорії сесії</CFormLabel>
								<Select
									isMulti
									options={catOptions}
									value={sessionCategories}
									onChange={setSessionCategories}
									isDisabled={saving}
									placeholder="Оберіть категорії..."
									isClearable
									classNamePrefix="react-select"
								/>
							</CCol>
						</CRow>
						<div className="d-flex gap-2 mt-3">
							<CButton
								color="success"
								onClick={handleSave}
								disabled={!hasChanges || saving}
								title="Зберегти зміни статусу та категорій"
							>
								{saving ? 'Збереження...' : 'Зберегти'}
							</CButton>
							<CButton
								color="secondary"
								variant="outline"
								onClick={handleCancel}
								disabled={!hasChanges || saving}
								title="Скасувати незбережені зміни"
							>
								Скасувати
							</CButton>
						</div>
					</FormSubmitOverlay>
				</CCardBody>
			</CCard>

			{/* ── Ланцюжок листів ── */}
			{loading ? (
				<CCard className="mb-4"><CCardBody><CenterLoader /></CCardBody></CCard>
			) : (
				<CCard className="mb-4">
					<CCardHeader>
						<strong>Листи сесії ({requests.length})</strong>
					</CCardHeader>
					<CCardBody>
						{requests.length === 0 && (
							<p className="text-muted">Листів поки немає.</p>
						)}
						{roots.map(root => (
							<div key={root.id} className="mb-3 border rounded p-2" style={{ borderColor: '#c8cbcf' }}>
								{renderRequestNode(root, 0)}
							</div>
						))}
					</CCardBody>
				</CCard>
			)}

			{/* ── Кнопки ── */}
			<div className="d-flex gap-2 mb-4">
				<CButton color="secondary" variant="outline" onClick={() => navigate(-1)} title="Повернутись до попередньої сторінки">
					← Назад
				</CButton>
				<CButton color="primary" onClick={openAddModal} title="Додати новий лист до цієї сесії">
					+ Додати лист до сесії
				</CButton>
			</div>

			<SupportLetterReplyModal
				open={replyModalOpen}
				onClose={() => setReplyModalOpen(false)}
				parentRequest={parentRequest}
				sessionId={parseInt(id)}
				subject={session?.subject}
				userEmail={session?.user_email}
				sessionRequests={requests}
				onSuccess={newRequest => setRequests(prev => [...prev, newRequest])}
				onStatusApplied={(requestId, newStatus) => setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: newStatus } : r))}
				clientData={parentRequest && parentRequest.sender_type !== 'admin'
					? { user_email: parentRequest.user_email, user_name: parentRequest.user_name, user_phone: parentRequest.user_phone }
					: sessionClientData
				}
				adminData={JSON.parse(localStorage.getItem('user_data') || '{}')}
			/>

			<SupportLettersModal
				open={modalOpen}
				requestId={modalRequestId}
				onClose={() => setModalOpen(false)}
			/>

			<SupportChangeSessionModal
				open={!!changeSessionRequest}
				onClose={() => setChangeSessionRequest(null)}
				requestId={changeSessionRequest?.id}
				currentSessionId={parseInt(id)}
				userEmail={changeSessionRequest?.user_email}
				onSuccess={handleChangeSessionSuccess}
			/>

			<SupportChangeParentModal
				open={!!changeParentRequest}
				onClose={() => setChangeParentRequest(null)}
				requestId={changeParentRequest?.id}
				currentParentId={changeParentRequest?.parent_id}
				sessionRequests={requests}
				onSuccess={handleChangeParentSuccess}
			/>

			<CModal visible={!!sessionChoiceModal} onClose={() => setSessionChoiceModal(null)}>
				<CModalHeader>
					<CModalTitle>Лист переміщено</CModalTitle>
				</CModalHeader>
				<CModalBody>
					Лист переміщено до сесії <strong>#{sessionChoiceModal?.newSessionId}</strong>
					{sessionChoiceModal?.newSessionSubject && <> «{sessionChoiceModal.newSessionSubject}»</>}.{' '}
					Куди перейти далі?
				</CModalBody>
				<CModalFooter>
					<CButton color="secondary" variant="outline" onClick={() => setSessionChoiceModal(null)}>
						Залишитися в сесії #{id}
					</CButton>
					<CButton color="primary" onClick={() => {
						const targetId = sessionChoiceModal?.newSessionId;
						setSessionChoiceModal(null);
						navigate(`${urlSupportLetters}/session/${targetId}`);
					}}>
						Перейти до сесії #{sessionChoiceModal?.newSessionId} →
					</CButton>
				</CModalFooter>
			</CModal>

		</>
	);
};

export default SupportLettersSession;
